import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { ROLE_LABELS } from '../lib/constants';
import ProfessionalLayout from '../components/ProfessionalLayout';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { DEFAULT_COUNTRIES, DEFAULT_CITIES_BY_COUNTRY, resolveCountryCode } from '../lib/countries';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { DataTable } from '../components/ui/data-table';
import { Skeleton } from '../components/ui/skeleton';
import {
  Mail, Shield, Ban, CheckCircle, MapPin, Building2, Filter, Plus, Save,
  AlertCircle, Activity, Users, UserCheck, UserX, Search, Crown, MoreHorizontal,
  ChevronDown, Lock, Eye, Package2, Phone, Upload, Download
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import PhoneInput from '../components/PhoneInput';

/** Format stored phone "+33612345678" → "+33 6 12 34 56 78" for display */
const formatPhoneDisplay = (phone) => {
  if (!phone) return '-';
  const hasPlus = phone.startsWith('+');
  const digits = phone.replace(/\D/g, '');
  if (!digits) return phone;
  const parts = [];
  let i = digits.length;
  while (i > 0) {
    const start = Math.max(0, i - 2);
    parts.unshift(digits.slice(start, i));
    i = start;
  }
  return (hasPlus ? '+' : '') + parts.join(' ');
};

const ROLE_CONFIG = {
  platform_admin: { level: 0, bg: 'bg-red-600', text: 'text-red-700', lightBg: 'bg-red-50', badgeBg: 'bg-red-100 text-red-700', permissions: 14 },
  super_admin: { level: 1, bg: 'bg-violet-600', text: 'text-violet-700', lightBg: 'bg-violet-50', badgeBg: 'bg-violet-100 text-violet-700', permissions: 12 },
  company_admin: { level: 2, bg: 'bg-blue-700', text: 'text-blue-700', lightBg: 'bg-blue-50', badgeBg: 'bg-blue-100 text-blue-700', permissions: 10 },
  country_admin: { level: 2, bg: 'bg-blue-700', text: 'text-blue-700', lightBg: 'bg-blue-50', badgeBg: 'bg-blue-100 text-blue-700', permissions: 8 },
  partner: { level: 3, bg: 'bg-teal-600', text: 'text-teal-700', lightBg: 'bg-teal-50', badgeBg: 'bg-teal-100 text-teal-700', permissions: 5 },
  local_partner: { level: 4, bg: 'bg-amber-500', text: 'text-amber-700', lightBg: 'bg-amber-50', badgeBg: 'bg-amber-100 text-amber-700', permissions: 5 },
  loader: { level: 4, bg: 'bg-cyan-600', text: 'text-cyan-700', lightBg: 'bg-cyan-50', badgeBg: 'bg-cyan-100 text-cyan-700', permissions: 4 },
  client: { level: 5, bg: 'bg-gray-500', text: 'text-gray-700', lightBg: 'bg-gray-50', badgeBg: 'bg-gray-100 text-gray-700', permissions: 3 },
};

const DEFAULT_ROLE_CONFIG = { level: 5, bg: 'bg-gray-500', text: 'text-gray-700', lightBg: 'bg-gray-50', badgeBg: 'bg-gray-100 text-gray-700', permissions: 3 };

const COUNTRY_ROLE_STYLES = {
  headquarter: { bg: 'bg-blue-700' },
  delivery: { bg: 'bg-amber-500' },
  both: { bg: 'bg-indigo-600' },
};

const getRoleConfig = (role) => ROLE_CONFIG[role] || DEFAULT_ROLE_CONFIG;

const getInitials = (name, email) => {
  if (name) {
    const names = name.split(' ');
    if (names.length >= 2) return `${names[0][0]}${names[1][0]}`.toUpperCase();
    return name.substring(0, 2).toUpperCase();
  }
  return email ? email.substring(0, 2).toUpperCase() : 'U';
};

const ROLE_DESCRIPTIONS = {
  super_admin: 'roleSuperAdminDesc',
  company_admin: 'roleCompanyAdminDesc',
  country_admin: 'roleCountryAdminDesc',
  partner: 'rolePartnerDesc',
  local_partner: 'roleLocalPartnerDesc',
  client: 'roleClientDesc',
};

const ROLE_ICONS = {
  super_admin: Crown,
  company_admin: Shield,
  country_admin: Shield,
  partner: Users,
  local_partner: MapPin,
  loader: Package2,
  client: Eye,
};

// Permissions matrix: module × role → permission level
const PERMISSION_LEVELS = {
  full: { key: 'permFull', className: 'bg-emerald-100 text-emerald-700' },
  edit: { key: 'permEdit', className: 'bg-blue-100 text-blue-700' },
  read: { key: 'permRead', className: 'bg-gray-100 text-gray-600' },
  scope: { key: 'permScope', className: 'bg-amber-100 text-amber-700' },
  self: { key: 'permSelf', className: 'bg-amber-100 text-amber-700' },
  none: { key: 'permNone', className: '' },
};

const MODULES = ['modShipments', 'modPackages', 'modFinance', 'modUsers', 'modPartners', 'modSettings', 'modTracking'];

const MATRIX_ROLES = ['platform_admin', 'super_admin', 'company_admin', 'country_admin', 'partner', 'local_partner', 'client'];

const PERMISSIONS_MATRIX = {
  modShipments:  { platform_admin: 'full', super_admin: 'full', company_admin: 'full', country_admin: 'edit', partner: 'scope', local_partner: 'read', client: 'read' },
  modPackages:   { platform_admin: 'full', super_admin: 'full', company_admin: 'full', country_admin: 'edit', partner: 'scope', local_partner: 'scope', client: 'read' },
  modFinance:    { platform_admin: 'full', super_admin: 'full', company_admin: 'full', country_admin: 'read', partner: 'none', local_partner: 'none', client: 'none' },
  modUsers:      { platform_admin: 'full', super_admin: 'full', company_admin: 'full', country_admin: 'none', partner: 'none', local_partner: 'none', client: 'none' },
  modPartners:   { platform_admin: 'full', super_admin: 'full', company_admin: 'edit', country_admin: 'read', partner: 'self', local_partner: 'self', client: 'none' },
  modSettings:   { platform_admin: 'full', super_admin: 'full', company_admin: 'full', country_admin: 'none', partner: 'none', local_partner: 'none', client: 'none' },
  modTracking:   { platform_admin: 'full', super_admin: 'full', company_admin: 'full', country_admin: 'full', partner: 'full', local_partner: 'full', client: 'read' },
};

const TeamOrgChartSections = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const roleLabels = (ROLE_LABELS[language] || ROLE_LABELS.fr);

  const getCountryRoleLabel = (countryRole) => {
    const labels = { headquarter: t('headquarters'), delivery: t('delivery'), both: t('headquartersAndDelivery') };
    return labels[countryRole] || countryRole;
  };

  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState(null);
  const importInputRef = React.useRef(null);
  const [suspendingUser, setSuspendingUser] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [mainTab, setMainTab] = useState('clients');
  const [showCountryFilters, setShowCountryFilters] = useState(false);
  const [filterExpedition, setFilterExpedition] = useState('all');
  const [filterDestination, setFilterDestination] = useState('all');
  const [assigningCountry, setAssigningCountry] = useState(false);
  const [headquarters, setHeadquarters] = useState([]);
  const [deliveryCountries, setDeliveryCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCountryRole, setSelectedCountryRole] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    name: '', email: '', password: '', role: 'client', phone: '', assigned_country: '', country_role: ''
  });
  const [packageClients, setPackageClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const apiUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchTeamMembers();
    fetchSections();
    fetchPackageClients();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(t('errorFetchingMembers'));
      const data = await response.json();
      setTeamMembers(data);
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSections = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/company/settings`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setHeadquarters(data.general?.headquarters || []);
        setDeliveryCountries(data.general?.delivery_countries || []);
      }
    } catch (err) {
      console.error('Error fetching sections:', err);
    }
  };

  const fetchPackageClients = async () => {
    try {
      setClientsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setPackageClients(data.clients || []);
      }
    } catch (err) {
      console.debug('Error fetching package clients:', err);
    } finally {
      setClientsLoading(false);
    }
  };

  // Modèle CSV à faire remplir aux clients puis à réimporter.
  // BOM UTF-8 pour qu'Excel garde les accents ; séparateur ';' (auto-détecté au back).
  const handleDownloadTemplate = () => {
    const rows = [
      'Nom;Téléphone;Email;Pays;Ville',
      'Jean Dupont;+33612345678;jean.dupont@example.com;France;Paris',
      'Awa Ngo;+237690000000;;Cameroun;Douala',
    ];
    const blob = new Blob(['﻿' + rows.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'modele_clients.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImportClients = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permet de re-sélectionner le même fichier
    if (!file) return;
    setError(null);
    setImportMsg(null);
    setImporting(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      // Pas de Content-Type manuel : le navigateur pose le boundary multipart.
      const response = await fetch(`${apiUrl}/api/clients/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || t('errorImport') || 'Erreur lors de l\'import');
      const parts = [`${data.imported} client(s) importé(s)`];
      if (data.skipped) parts.push(`${data.skipped} ignoré(s)`);
      if (data.errors?.length) parts.push(`${data.errors.length} erreur(s)`);
      setImportMsg(parts.join(' · '));
      fetchPackageClients();
      setTimeout(() => setImportMsg(null), 8000);
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'import');
    } finally {
      setImporting(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      setCreatingUser(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/users/create-user`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newUserForm,
          country: newUserForm.assigned_country || null,
          // "__all__" = toutes les villes → aucune ville spécifique
          city: (newUserForm.city && newUserForm.city !== '__all__') ? newUserForm.city : null,
          assigned_country: newUserForm.assigned_country || null,
          country_role: newUserForm.country_role || null,
        })
      });
      if (!response.ok) {
        const errorData = await response.json();
        const detail = errorData.detail;
        const msg = typeof detail === 'string' ? detail
          : Array.isArray(detail) ? detail.map(d => d.msg || JSON.stringify(d)).join(', ')
          : t('errorCreatingUser');
        throw new Error(msg);
      }
      setNewUserForm({ name: '', email: '', password: '', role: 'client', phone: '', city: '', assigned_country: '', country_role: '' });
      setShowCreateModal(false);
      await fetchTeamMembers();
    } catch (err) {
      console.error('Error creating user:', err);
      setError(err.message);
    } finally {
      setCreatingUser(false);
    }
  };

  const assignUserToCountry = async (userId) => {
    if (!selectedCountry || !selectedCountryRole) { alert(t('selectCountryAndRole')); return; }
    try {
      setAssigningCountry(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/users/${userId}/country-assignment`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_country: selectedCountry, country_role: selectedCountryRole })
      });
      if (!response.ok) throw new Error(t('errorAssigningCountry'));
      await fetchTeamMembers();
      setSelectedMember(null);
      setSelectedCountry('');
      setSelectedCountryRole('');
    } catch (err) {
      console.error('Error assigning country:', err);
      alert(err.message);
    } finally {
      setAssigningCountry(false);
    }
  };

  const toggleSuspension = async (userId, currentStatus) => {
    try {
      setSuspendingUser(userId);
      const token = localStorage.getItem('token');
      const endpoint = currentStatus ? 'unsuspend' : 'suspend';
      const response = await fetch(`${apiUrl}/api/users/${userId}/${endpoint}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: currentStatus ? t('reactivation') : t('suspendedByAdmin') })
      });
      if (!response.ok) throw new Error(t('errorStatusChange'));
      await fetchTeamMembers();
    } catch (err) {
      console.error('Error toggling suspension:', err);
      alert(t('error') + ': ' + err.message);
    } finally {
      setSuspendingUser(null);
    }
  };

  const canSuspendUser = (targetRole) => {
    const targetLevel = getRoleConfig(targetRole).level;
    if (user?.role === 'platform_admin' && targetLevel > 0) return true;
    if (user?.role === 'super_admin' && targetLevel > 1) return true;
    if ((user?.role === 'company_admin' || user?.role === 'country_admin') && targetLevel > 2) return true;
    return false;
  };

  const getExpeditionCountries = () => headquarters.map(hq => hq.name || hq.country_name).filter(Boolean).sort();
  const getDestinationCountries = () => deliveryCountries.map(dc => dc.name || dc.country_name).filter(Boolean).sort();

  // Counts by tab — clients come from packages, not users
  const clientsCount = packageClients.length;
  const teamCount = teamMembers.filter(m => m.role !== 'client').length;
  const rolesInUse = [...new Set(teamMembers.map(m => m.role))].length;

  // Counts for role filter tabs
  const getRoleCounts = () => {
    const base = mainTab === 'clients' ? teamMembers.filter(m => m.role === 'client') : mainTab === 'team' ? teamMembers.filter(m => m.role !== 'client') : teamMembers;
    return {
      all: base.length,
      admins: base.filter(m => ['super_admin', 'company_admin', 'country_admin'].includes(m.role)).length,
      collectors: base.filter(m => m.role === 'partner').length,
      delivery: base.filter(m => m.role === 'local_partner').length,
      clients: base.filter(m => m.role === 'client').length,
    };
  };

  const getFilteredMembers = () => {
    return teamMembers.filter(member => {
      // Clients tab uses packageClients, not users
      if (mainTab === 'clients') return false;
      if (mainTab === 'team' && member.role === 'client') return false;

      // Search
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (!(member.name || '').toLowerCase().includes(q) && !(member.email || '').toLowerCase().includes(q)) return false;
      }

      // Role filter
      if (roleFilter !== 'all') {
        if (roleFilter === 'admins' && !['super_admin', 'company_admin', 'country_admin'].includes(member.role)) return false;
        if (roleFilter === 'collectors' && member.role !== 'partner') return false;
        if (roleFilter === 'delivery' && member.role !== 'local_partner') return false;
        if (roleFilter === 'clients' && member.role !== 'client') return false;
      }

      // Country filters
      if (filterExpedition !== 'all' || filterDestination !== 'all') {
        let matchesExpedition = true;
        if (filterExpedition !== 'all') {
          matchesExpedition = member.assigned_country === filterExpedition &&
            (member.country_role === 'headquarter' || member.country_role === 'both');
        }
        let matchesDestination = true;
        if (filterDestination !== 'all') {
          matchesDestination = member.assigned_country === filterDestination &&
            (member.country_role === 'delivery' || member.country_role === 'both');
        }
        if (filterExpedition !== 'all' && filterDestination !== 'all') return matchesExpedition || matchesDestination;
        return matchesExpedition && matchesDestination;
      }

      return true;
    });
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-80" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
          </div>
          <Skeleton className="h-14 w-full rounded-xl" />
          <Skeleton className="h-96 w-full rounded-xl" />
        </div>
      </ProfessionalLayout>
    );
  }

  const filtered = getFilteredMembers();
  const roleCounts = getRoleCounts();

  // Role filter tabs config (depends on mainTab)
  const roleFilterTabs = mainTab === 'clients'
    ? [] // Clients tab = all clients, no sub-filter needed
    : [
        { key: 'all', label: t('filterAll'), count: roleCounts.all },
        { key: 'admins', label: t('filterAdmins'), count: roleCounts.admins },
        { key: 'collectors', label: t('filterCollectors'), count: roleCounts.collectors },
        { key: 'delivery', label: t('filterDelivery'), count: roleCounts.delivery },
      ];

  // Roles data for the Roles tab
  const allRoles = ['super_admin', 'company_admin', 'country_admin', 'partner', 'local_partner', 'client'];

  return (
    <ProfessionalLayout>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('tabUsers')}</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {mainTab === 'clients' ? t('clientsSubtitle') : `${teamMembers.length} ${teamMembers.length > 1 ? t('members') : t('member')} ${t('inYourTeam')}`}
          </p>
        </div>
        {(user?.role === 'company_admin' || user?.role === 'super_admin') && (
          <div className="flex items-center gap-2 flex-wrap">
            {mainTab === 'clients' && (
              <>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  className="hidden"
                  onChange={handleImportClients}
                />
                <Button variant="outline" onClick={handleDownloadTemplate}>
                  <Download size={18} className="mr-1" />
                  Modèle
                </Button>
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="outline"
                        onClick={() => importInputRef.current?.click()}
                        disabled={importing}
                      >
                        <Upload size={18} className="mr-1" />
                        {importing ? 'Import…' : 'Importer les clients'}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs text-left">
                      <p className="font-semibold mb-1">Format attendu (CSV ou Excel)</p>
                      <p>Colonnes obligatoires : <strong>Nom</strong>, <strong>Téléphone</strong></p>
                      <p>Colonnes optionnelles : Email, Pays, Ville</p>
                      <p className="mt-1 opacity-80">Téléchargez le « Modèle », faites-le remplir, puis réimportez-le. Les doublons (même téléphone) sont ignorés.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus size={18} />
              {t('addMember')}
            </Button>
          </div>
        )}
      </div>

      {importMsg && (
        <Alert className="mb-4 border-green-300 bg-green-50 text-green-800">
          <CheckCircle className="h-4 w-4" />
          <AlertDescription>{importMsg}</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Main Tabs */}
      <div className="flex items-center gap-1 border-b border-border mb-6">
        {[
          { key: 'clients', label: t('tabClients'), count: clientsCount },
          { key: 'team', label: t('tabTeam'), count: teamCount },
          { key: 'roles', label: t('tabRoles'), count: rolesInUse },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => { setMainTab(tab.key); setRoleFilter('all'); setSearchQuery(''); }}
            className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
              mainTab === tab.key
                ? 'text-foreground after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            <Badge variant="secondary" className="ml-2 text-[10px] px-1.5 py-0">{tab.count}</Badge>
          </button>
        ))}
      </div>

      {/* Roles & Permissions Tab */}
      {mainTab === 'roles' ? (
        <div className="space-y-6">
          <p className="text-sm text-muted-foreground">{t('rolesIntro')}</p>

          {/* Role summary cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {allRoles.map(role => {
              const config = getRoleConfig(role);
              const RoleIcon = ROLE_ICONS[role] || Shield;
              const count = teamMembers.filter(m => m.role === role).length;
              return (
                <Card key={role} className="border-border">
                  <CardContent className="p-3 text-center">
                    <div className={`w-9 h-9 rounded-lg grid place-items-center mx-auto mb-2 ${config.lightBg} ${config.text}`}>
                      <RoleIcon size={18} />
                    </div>
                    <p className="text-xs font-semibold text-foreground truncate">{roleLabels[role] || role}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{count} {t('membersWithRole')}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Permissions Matrix Table */}
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3 sticky left-0 bg-background min-w-[140px]">
                      {t('permModule')}
                    </th>
                    {MATRIX_ROLES.map(role => {
                      const config = getRoleConfig(role);
                      return (
                        <th key={role} className="text-center text-[11px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-3 min-w-[100px]">
                          <Badge className={`${config.badgeBg} text-[10px] font-medium`}>
                            {roleLabels[role] || role}
                          </Badge>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {MODULES.map((mod, idx) => (
                    <tr key={mod} className={`border-b border-border last:border-0 ${idx % 2 === 0 ? '' : 'bg-muted/30'}`}>
                      <td className="px-4 py-3 text-sm font-medium text-foreground sticky left-0 bg-inherit">
                        {t(mod)}
                      </td>
                      {MATRIX_ROLES.map(role => {
                        const level = PERMISSIONS_MATRIX[mod][role];
                        const perm = PERMISSION_LEVELS[level];
                        return (
                          <td key={role} className="px-3 py-3 text-center">
                            {level === 'none' ? (
                              <span className="text-muted-foreground/40 text-sm">—</span>
                            ) : (
                              <Badge className={`${perm.className} text-[10px] font-medium`}>
                                {t(perm.key)}
                              </Badge>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          {(() => {
            if (mainTab === 'clients') {
              // Clients KPIs — derived from packages, not users
              const totalClients = packageClients.length;
              const totalPackages = packageClients.reduce((sum, c) => sum + (c.packages_count || 0), 0);
              const withEmail = packageClients.filter(c => c.email).length;
              const avgPackages = totalClients > 0 ? (totalPackages / totalClients).toFixed(1) : 0;

              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <Card className="border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-4">
                        <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight">
                          {language === 'fr' ? 'Total clients' : 'Total clients'}
                        </span>
                        <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0 bg-blue-50 text-blue-600">
                          <Users size={18} />
                        </div>
                      </div>
                      <div className="text-[28px] font-bold text-foreground leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>{totalClients}</div>
                      <p className="text-[11px] text-muted-foreground mt-1.5">
                        {language === 'fr' ? 'Expéditeurs uniques' : 'Unique senders'}
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-4">
                        <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight">
                          {language === 'fr' ? 'Colis déposés' : 'Packages deposited'}
                        </span>
                        <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0 bg-violet-50 text-violet-600">
                          <Package2 size={18} />
                        </div>
                      </div>
                      <div className="text-[28px] font-bold text-foreground leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>{totalPackages}</div>
                      <p className="text-[11px] text-muted-foreground mt-1.5">
                        {language === 'fr' ? 'Total tous clients' : 'Total all clients'}
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-4">
                        <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight">
                          {language === 'fr' ? 'Moy. colis/client' : 'Avg packages/client'}
                        </span>
                        <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0 bg-amber-50 text-amber-600">
                          <Activity size={18} />
                        </div>
                      </div>
                      <div className="text-[28px] font-bold text-foreground leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>{avgPackages}</div>
                      <p className="text-[11px] text-muted-foreground mt-1.5">
                        {language === 'fr' ? 'Colis par client' : 'Packages per client'}
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-border">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-4">
                        <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight">
                          {language === 'fr' ? 'Avec email' : 'With email'}
                        </span>
                        <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0 bg-emerald-50 text-emerald-600">
                          <Mail size={18} />
                        </div>
                      </div>
                      <div className="text-[28px] font-bold text-foreground leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>{withEmail}</div>
                      <p className="text-[11px] text-muted-foreground mt-1.5">
                        {language === 'fr' ? `sur ${totalClients} clients` : `out of ${totalClients} clients`}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              );
            }

            // Team tab KPIs
            const tabBase = teamMembers.filter(m => m.role !== 'client');
            const activeCount = tabBase.filter(m => !m.is_suspended).length;
            const suspendedCount = tabBase.filter(m => m.is_suspended).length;
            const countriesSet = new Set(tabBase.map(m => m.assigned_country).filter(Boolean));
            const adminsCount = tabBase.filter(m => ['super_admin', 'company_admin', 'country_admin'].includes(m.role)).length;

            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <Card className="border-border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight">
                        {t('teamActiveMembers')}
                      </span>
                      <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0 bg-blue-50 text-blue-600">
                        <Users size={18} />
                      </div>
                    </div>
                    <div className="text-[28px] font-bold text-foreground leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>{activeCount}</div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        {language === 'fr' ? 'Actifs' : 'Active'}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight">
                        {t('teamAdmins')}
                      </span>
                      <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0 bg-violet-50 text-violet-600">
                        <Crown size={18} />
                      </div>
                    </div>
                    <div className="text-[28px] font-bold text-foreground leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>{adminsCount}</div>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        {language === 'fr' ? 'Actifs' : 'Active'}
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight">
                        {language === 'fr' ? 'Pays couverts' : 'Countries covered'}
                      </span>
                      <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0 bg-amber-50 text-amber-600">
                        <MapPin size={18} />
                      </div>
                    </div>
                    <div className="text-[28px] font-bold text-foreground leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>{countriesSet.size}</div>
                    <p className="text-[11px] text-muted-foreground mt-1.5">
                      {countriesSet.size > 0 ? [...countriesSet].slice(0, 3).join(', ') : t('noAssignment')}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-border">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-4">
                      <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-wider leading-tight">
                        {t('teamSuspendedMembers')}
                      </span>
                      <div className="w-9 h-9 rounded-lg grid place-items-center shrink-0 bg-red-50 text-red-600">
                        <UserX size={18} />
                      </div>
                    </div>
                    <div className="text-[28px] font-bold text-foreground leading-none" style={{ fontVariantNumeric: 'tabular-nums' }}>{suspendedCount}</div>
                    <p className="text-[11px] text-muted-foreground mt-1.5">
                      {language === 'fr' ? `sur ${tabBase.length} membres` : `out of ${tabBase.length} members`}
                    </p>
                  </CardContent>
                </Card>
              </div>
            );
          })()}

          {/* Search + Role filter tabs + Filtres button */}
          <div className="flex items-center gap-3 flex-wrap mb-4">
            <div className="relative flex-1 min-w-0 w-full sm:w-auto">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('searchMembers')}
                className="pl-9 h-10"
              />
            </div>
            {roleFilterTabs.length > 0 && (
              <div className="flex gap-1">
                {roleFilterTabs.map(tab => (
                  <Button
                    key={tab.key}
                    variant={roleFilter === tab.key ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setRoleFilter(tab.key)}
                    className="text-xs h-8"
                  >
                    {tab.label}
                    <span className="ml-1 opacity-60">· {tab.count}</span>
                  </Button>
                ))}
              </div>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setShowCountryFilters(!showCountryFilters)}
            >
              <Filter size={14} />
              Filtres
              <ChevronDown size={14} className={`transition-transform ${showCountryFilters ? 'rotate-180' : ''}`} />
            </Button>
          </div>

          {/* Country filters (collapsible) */}
          {showCountryFilters && (
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex-1 min-w-0 w-full sm:w-auto">
                    <Label className="mb-1.5 block text-xs text-muted-foreground font-medium">{t('shippingCountriesHQ')}</Label>
                    <Select value={filterExpedition} onValueChange={setFilterExpedition}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('allShippingCountries')}</SelectItem>
                        {getExpeditionCountries().map(country => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <span className="text-lg font-bold text-muted-foreground hidden sm:block">→</span>
                  <div className="flex-1 min-w-0 w-full sm:w-auto">
                    <Label className="mb-1.5 block text-xs text-muted-foreground font-medium">{t('destinationCountriesDelivery')}</Label>
                    <Select value={filterDestination} onValueChange={setFilterDestination}>
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('allDestinationCountries')}</SelectItem>
                        {getDestinationCountries().map(country => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cards / DataTable Grid */}
          {mainTab === 'clients' ? (
            /* Clients from packages — DataTable */
            clientsLoading ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}
              </div>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <DataTable
                    data={packageClients}
                    searchPlaceholder={language === 'fr' ? 'Rechercher un client (nom, email, tél)...' : 'Search client (name, email, phone)...'}
                    searchColumn="name"
                    globalFilter={searchQuery}
                    onGlobalFilterChange={setSearchQuery}
                    pageSize={20}
                    emptyMessage={language === 'fr' ? 'Aucun client trouvé' : 'No clients found'}
                    columns={[
                      {
                        accessorKey: 'name',
                        header: language === 'fr' ? 'Nom' : 'Name',
                        cell: ({ row }) => (
                          <div className="flex items-center gap-2">
                            <Avatar className="w-7 h-7 bg-blue-600">
                              <AvatarFallback className="bg-blue-600 text-white font-bold text-[10px]">
                                {getInitials(row.original.name, row.original.email)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="font-medium">{row.original.name || '-'}</span>
                          </div>
                        ),
                      },
                      {
                        accessorKey: 'phone',
                        header: language === 'fr' ? 'Téléphone' : 'Phone',
                        cell: ({ getValue }) => (
                          <span className="text-muted-foreground font-mono text-xs tracking-wide">
                            {formatPhoneDisplay(getValue())}
                          </span>
                        ),
                      },
                      {
                        accessorKey: 'email',
                        header: 'Email',
                        cell: ({ getValue }) => getValue()
                          ? <a href={`mailto:${getValue()}`} className="text-blue-600 hover:underline text-xs">{getValue()}</a>
                          : <span className="text-muted-foreground">-</span>,
                      },
                      {
                        accessorKey: 'packages_count',
                        header: language === 'fr' ? 'Colis' : 'Packages',
                        cell: ({ getValue }) => (
                          <Badge className="bg-blue-100 text-blue-700 text-[10px]">{getValue()}</Badge>
                        ),
                      },
                      {
                        accessorKey: 'last_package_at',
                        header: language === 'fr' ? 'Dernier dépôt' : 'Last deposit',
                        cell: ({ getValue }) => {
                          const v = getValue();
                          if (!v) return '-';
                          return new Date(v).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                            day: '2-digit', month: 'short', year: 'numeric',
                          });
                        },
                      },
                      {
                        accessorKey: 'first_package_at',
                        header: language === 'fr' ? 'Client depuis' : 'Client since',
                        cell: ({ getValue }) => {
                          const v = getValue();
                          if (!v) return '-';
                          return new Date(v).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
                            day: '2-digit', month: 'short', year: 'numeric',
                          });
                        },
                      },
                    ]}
                  />
                </CardContent>
              </Card>
            )
          ) : (
            /* Team members (non-client users) */
            filtered.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-12 text-center">
                  <Users size={36} className="mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">{t('noMembersInFilter')}</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filtered.map(member => {
                    const config = getRoleConfig(member.role);
                    return (
                      <Card key={member.id} className="border-border hover:shadow-md transition-shadow">
                        <CardContent className="p-0">
                          <div className="p-4 pb-3">
                            <div className="flex items-start gap-3">
                              <div className="relative">
                                <Avatar className={`w-11 h-11 ${config.bg}`}>
                                  <AvatarFallback className={`${config.bg} text-white font-bold text-sm`}>
                                    {getInitials(member.name, member.email)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${member.is_suspended ? 'bg-red-500' : 'bg-emerald-500'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-semibold text-foreground truncate">{member.name || t('noName')}</p>
                                  <Badge className={`shrink-0 text-[10px] px-1.5 py-0 ${member.is_suspended ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                    {member.is_suspended ? t('statusSuspended') : t('statusActive')}
                                  </Badge>
                                </div>
                                <p className="text-xs text-muted-foreground truncate mt-0.5">
                                  {member.email}
                                  {member.assigned_country && ` · ${member.assigned_country}`}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="px-4 py-2.5 border-t border-border bg-muted/30 flex items-center gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{t('columnRole')}</div>
                              <Badge className={`${config.badgeBg} text-[10px] font-medium mt-0.5`}>
                                {roleLabels[member.role] || member.role}
                              </Badge>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{t('columnScope')}</div>
                              <p className="text-xs font-medium text-foreground mt-0.5 truncate">
                                {member.assigned_country ? `${member.assigned_country}` : t('noAssignment')}
                              </p>
                            </div>
                            {member.country_role && (
                              <div className="flex-1 min-w-0">
                                <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">{t('type')}</div>
                                <p className="text-xs font-medium text-foreground mt-0.5 truncate">
                                  {getCountryRoleLabel(member.country_role)}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="px-4 py-2.5 border-t border-border flex items-center gap-2">
                            <Button variant="ghost" size="sm" className="text-xs h-7 flex-1" onClick={() => setSelectedMember(member)}>
                              {t('memberDetails')}
                            </Button>
                            {member.email && (
                              <Button variant="ghost" size="sm" className="text-xs h-7 flex-1" onClick={() => window.location.href = `mailto:${member.email}`}>
                                <Mail size={12} className="mr-1" />
                                {t('email')}
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="text-xs h-7 px-2" onClick={() => setSelectedMember(member)}>
                              <MoreHorizontal size={14} />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
                <div className="mt-4 text-center">
                  <p className="text-xs text-muted-foreground">
                    {filtered.length} {filtered.length > 1 ? t('membersFound') : t('memberFound')}
                  </p>
                </div>
              </>
            )
          )}
        </>
      )}

      {/* Dialog Details */}
      <Dialog open={!!selectedMember} onOpenChange={(open) => { if (!open) { setSelectedMember(null); setSelectedCountry(''); setSelectedCountryRole(''); } }}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
          {selectedMember && (() => {
            const config = getRoleConfig(selectedMember.role);
            const canSuspend = canSuspendUser(selectedMember.role) && selectedMember.id !== user?.id;

            return (
              <>
                <DialogHeader>
                  <DialogTitle className="sr-only">{t('memberDetails')}</DialogTitle>
                  <DialogDescription className="sr-only">
                    {t('detailedInfoAbout')} {selectedMember.name || selectedMember.email}
                  </DialogDescription>
                </DialogHeader>

                <div className="flex items-start gap-4">
                  <Avatar className={`w-16 h-16 ${config.bg}`}>
                    <AvatarFallback className={`${config.bg} text-white font-bold text-2xl`}>
                      {getInitials(selectedMember.name, selectedMember.email)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold truncate">{selectedMember.name || t('noName')}</h3>
                    <div className="flex items-center gap-2 text-muted-foreground text-sm mt-1">
                      <Mail size={14} />
                      <span className="truncate">{selectedMember.email}</span>
                    </div>
                    {selectedMember.phone && (
                      <div className="flex items-center gap-2 text-muted-foreground text-sm mt-0.5">
                        <Phone size={14} />
                        <span className="font-mono tracking-wide">{formatPhoneDisplay(selectedMember.phone)}</span>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      <Badge className={config.badgeBg}>
                        <Shield size={12} className="mr-1" />
                        {roleLabels[selectedMember.role] || selectedMember.role}
                      </Badge>
                      {selectedMember.assigned_country && COUNTRY_ROLE_STYLES[selectedMember.country_role] && (
                        <Badge className={`${COUNTRY_ROLE_STYLES[selectedMember.country_role].bg} text-white`}>
                          <Building2 size={12} className="mr-1" />
                          {selectedMember.assigned_country} - {getCountryRoleLabel(selectedMember.country_role)}
                        </Badge>
                      )}
                      {(selectedMember.city || selectedMember.country) && (
                        <Badge variant="outline" className={`${config.text} border-current`}>
                          <MapPin size={12} className="mr-1" />
                          {[selectedMember.city, selectedMember.country].filter(Boolean).join(', ')}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>

                <div className={`flex items-center gap-2 p-3 rounded-lg ${selectedMember.is_suspended ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                  {selectedMember.is_suspended ? (
                    <><Ban size={16} /><span className="font-medium text-sm">{t('accountSuspended')}</span></>
                  ) : (
                    <><CheckCircle size={16} /><span className="font-medium text-sm">{t('accountActive')}</span></>
                  )}
                </div>

                {selectedMember.role === 'local_partner' &&
                  ['super_admin', 'company_admin', 'country_admin'].includes(user?.role) && (
                  <Button variant="outline" className="w-full" onClick={() => { navigate(`/admin/delivery-partners/${selectedMember.id}`); setSelectedMember(null); }}>
                    <Activity size={16} className="mr-2" />
                    {t('viewActivity')}
                  </Button>
                )}

                {selectedMember.role === 'partner' &&
                  ['super_admin', 'company_admin', 'country_admin'].includes(user?.role) && (
                  <Button variant="outline" className="w-full" onClick={() => { navigate(`/admin/collectors/${selectedMember.id}`); setSelectedMember(null); }}>
                    <Activity size={16} className="mr-2" />
                    {t('viewActivity')}
                  </Button>
                )}

                {(user?.role === 'company_admin' || user?.role === 'super_admin') && (
                  <Card className="border-primary/30">
                    <CardContent className="p-4 space-y-3">
                      <h4 className="font-bold flex items-center gap-2">
                        <Building2 size={16} />{t('countryAssignment')}
                      </h4>
                      {selectedMember.assigned_country && COUNTRY_ROLE_STYLES[selectedMember.country_role] && (
                        <div className="flex items-center gap-2 p-2 bg-muted rounded text-sm">
                          <span className="font-semibold text-muted-foreground">{t('current')} :</span>
                          <Badge className={`${COUNTRY_ROLE_STYLES[selectedMember.country_role].bg} text-white`}>
                            <Building2 size={10} className="mr-1" />
                            {selectedMember.assigned_country} - {getCountryRoleLabel(selectedMember.country_role)}
                          </Badge>
                        </div>
                      )}
                      <div className="space-y-3">
                        <div>
                          <Label className="mb-1.5 block">{t('country')}</Label>
                          <Select value={selectedCountry || undefined} onValueChange={setSelectedCountry}>
                            <SelectTrigger><SelectValue placeholder={t('selectCountry')} /></SelectTrigger>
                            <SelectContent>
                              {DEFAULT_COUNTRIES.map((c) => (
                                <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="mb-1.5 block">{t('type')}</Label>
                          <Select value={selectedCountryRole || undefined} onValueChange={setSelectedCountryRole}>
                            <SelectTrigger><SelectValue placeholder={t('selectType')} /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="headquarter">{t('headquartersShipping')}</SelectItem>
                              <SelectItem value="delivery">{t('delivery')}</SelectItem>
                              <SelectItem value="both">{t('both')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={() => assignUserToCountry(selectedMember.id)} disabled={!selectedCountry || !selectedCountryRole || assigningCountry} className="w-full">
                          {assigningCountry ? t('assigning') : t('assignToCountry')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {canSuspend && (
                  <Button
                    variant={selectedMember.is_suspended ? 'secondary' : 'destructive'}
                    className="w-full"
                    onClick={() => { toggleSuspension(selectedMember.id, selectedMember.is_suspended); setSelectedMember(null); }}
                    disabled={suspendingUser === selectedMember.id}
                  >
                    {suspendingUser === selectedMember.id ? t('processing') : selectedMember.is_suspended ? (
                      <><CheckCircle size={16} />{t('reactivateAccount')}</>
                    ) : (
                      <><Ban size={16} />{t('suspendAccount')}</>
                    )}
                  </Button>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Dialog Creation */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('addTeamMember')}</DialogTitle>
            <DialogDescription>{t('fillInfoToCreateMember')}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label htmlFor="create-name" className="mb-1.5 block">{t('fullName')} *</Label>
              <Input id="create-name" value={newUserForm.name} onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })} required placeholder="Ex: Jean Dupont" />
            </div>
            <div>
              <Label htmlFor="create-email" className="mb-1.5 block">{t('email')} *</Label>
              <Input id="create-email" type="email" value={newUserForm.email} onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })} required placeholder="Ex: jean@entreprise.com" />
            </div>
            <div>
              <Label htmlFor="create-password" className="mb-1.5 block">{t('password')} *</Label>
              <Input id="create-password" type="password" value={newUserForm.password} onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })} required placeholder={t('minCharsPassword')} />
            </div>
            <PhoneInput
              label={t('phone')}
              value={newUserForm.phone}
              onChange={(val) => setNewUserForm({ ...newUserForm, phone: val })}
              defaultCountryCode="FR"
              id="create-phone"
            />
            <div>
              <Label className="mb-1.5 block">{t('role')} *</Label>
              <Select value={newUserForm.role} onValueChange={(val) => setNewUserForm({ ...newUserForm, role: val })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">{roleLabels.client}</SelectItem>
                  <SelectItem value="partner">{t('collectionPartner')}</SelectItem>
                  <SelectItem value="local_partner">{t('deliveryPartner')}</SelectItem>
                  <SelectItem value="loader">{roleLabels.loader}</SelectItem>
                  <SelectItem value="country_admin">{roleLabels.country_admin}</SelectItem>
                  {user?.role === 'super_admin' && (
                    <SelectItem value="company_admin">{roleLabels.company_admin}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {(newUserForm.role === 'country_admin' || newUserForm.role === 'partner' || newUserForm.role === 'local_partner') && (
              <Card className={`${newUserForm.role === 'partner' ? 'border-teal-300' : newUserForm.role === 'local_partner' ? 'border-amber-300' : 'border-primary/30'}`}>
                <CardContent className="p-4 space-y-3">
                  <p className="text-sm font-semibold text-primary">
                    {newUserForm.role === 'partner' && t('countryAssignment')}
                    {newUserForm.role === 'local_partner' && t('assignToDeliveryCountry')}
                    {newUserForm.role === 'country_admin' && t('countryAssignment')}
                  </p>
                  <div>
                    <Label className="mb-1.5 block">{t('country')} *</Label>
                    <Select
                      value={newUserForm.assigned_country || undefined}
                      onValueChange={(val) => {
                        let autoRole = newUserForm.country_role;
                        if (newUserForm.role === 'local_partner') autoRole = 'delivery';
                        setNewUserForm({ ...newUserForm, assigned_country: val, country_role: autoRole, city: '__all__' });
                      }}
                    >
                      <SelectTrigger><SelectValue placeholder={t('selectCountry')} /></SelectTrigger>
                      <SelectContent>
                        {DEFAULT_COUNTRIES.map((c) => (
                          <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(newUserForm.role === 'partner' || newUserForm.role === 'local_partner') && newUserForm.assigned_country && (
                    <div>
                      <Label className="mb-1.5 block">{t('city')}</Label>
                      <Select
                        value={newUserForm.city || '__all__'}
                        onValueChange={(val) => setNewUserForm({ ...newUserForm, city: val })}
                      >
                        <SelectTrigger><SelectValue placeholder={language === 'fr' ? 'Sélectionnez une ville' : 'Select a city'} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__all__">{language === 'fr' ? 'Toutes les villes' : 'All cities'}</SelectItem>
                          {(DEFAULT_CITIES_BY_COUNTRY[resolveCountryCode(newUserForm.assigned_country)] || []).map((city) => (
                            <SelectItem key={city.code} value={city.name}>{city.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {newUserForm.role === 'country_admin' && (
                    <div>
                      <Label className="mb-1.5 block">{t('type')} *</Label>
                      <Select value={newUserForm.country_role || undefined} onValueChange={(val) => setNewUserForm({ ...newUserForm, country_role: val })}>
                        <SelectTrigger><SelectValue placeholder={t('selectType')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="headquarter">{t('headquartersShipping')}</SelectItem>
                          <SelectItem value="delivery">{t('delivery')}</SelectItem>
                          <SelectItem value="both">{t('both')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="submit" disabled={creatingUser} className="flex-1">
                <Save size={16} />
                {creatingUser ? t('creating') : t('createMember')}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                {t('cancel')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </ProfessionalLayout>
  );
};

export default TeamOrgChartSections;
